import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Container, Row, Col, Spinner } from 'react-bootstrap';
import Image from 'react-bootstrap/Image';
import UsuarioExternoService from '../../services/usuarioExternoService';
import UsuarioService from '../../services/usuarioService';
import Botao from '../../shared/botao';
import styled from 'styled-components';
import Rodape from '../../shared/rodape';
import CustomAlert from '../../shared/customAlert';
import VersaoSistemaService from '../../services/versaoSistemaService';
import { useLayout } from '../../../contexts/LayoutContext';
import { Originador } from '../../shared/model/originador';

const usuarioExternoService = new UsuarioExternoService();
const usuarioService = new UsuarioService();
const versaoSistemaService = new VersaoSistemaService();

const LoginComponent = () => {
  const { layout } = useLayout();
  
  const loginRef = useRef<HTMLInputElement>(null);
  const senhaRef = useRef<HTMLInputElement>(null);
  const originadorRef = useRef<HTMLSelectElement>(null); 

  const navigate = useNavigate();
  const { slug } = useParams(); 

  const [alert, setAlert] = useState<{message: string; type: 'success' | 'warning' | 'error'; action?: () => void; } | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  
  const [showOriginadorInput, setShowOriginadorInput] = useState(false);
  const [listaOriginadores, setListaOriginadores] = useState<any[]>([]); 
  const [originadorSelecionado, setOriginadorSelecionado] = useState(''); 

  const [form, setForm] = useState<{ login: string; senha: string }>({
    login: '',
    senha: '',
  });

  const [apiVersion, setApiVersion] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  const carregarOriginadores = async (idUsuario: string) => {
    try {
      const response = await usuarioExternoService.listarUsuarioComOrigemPorUsuario(idUsuario);
      if (response && response.data) {
        setListaOriginadores(response.data);
      }
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao carregar lista de originadores.', type: 'error' });
    }
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    try {
      setLoading(true);
      const login = loginRef.current?.value || '';
      const senha = senhaRef.current?.value || '';
      
      const originadorId = showOriginadorInput ? originadorSelecionado : null;

      if (showOriginadorInput && !originadorId) {
         setLoading(false);
         setAlert({ message: 'Selecione um originador para continuar.', type: 'warning' });
         return;
      }

      const response = await usuarioExternoService.login({ 
        login, 
        senha,
        whitelabel: !!slug,
        whitelabelSlug: slug || null,
        originadorId: originadorId 
      });

      if (response) {
        if (response.slug) {
            navigate(`/${response.slug}/home`);
        } else {
            navigate(slug ? `/${slug}/home` : '/home');
        }
      }
      setLoading(false);

    } catch (err: any) {
      setLoading(false);
      
      const errorMessage = err.response?.data || '';

      if (typeof errorMessage === 'string' && errorMessage.includes(`obrigatório informar a ${Originador.DESCRITIVO_GERAL}`)) {
          const login = loginRef.current?.value || '';
          await carregarOriginadores(login);
          setShowOriginadorInput(true);
          return;
      }

      if (!apiVersion || apiVersion === null)
        setAlert({ message: 'Servidor indisponível', type: 'error', action: () => window.location.reload() });
      else if (err?.code === 'ERR_NETWORK') 
        setAlert({ message: 'Verifique a conexão com a internet e tente novamente', type: 'warning', action: () => window.location.reload() });  
      else if (err?.response?.status === 412) {
        const login = loginRef.current?.value || '';
        setForm((prev) => ({ ...prev, login })); 
        setAlert({
          message: err.response?.data,
          type: 'warning',
          action: () => navigate('/trocar-senha', { state: { login } }),
        });
      }
      else 
        setAlert({ message: errorMessage || 'Erro desconhecido', type: 'error', action: () => window.location.reload() });
    }
  };

  const handleCloseAlert = () => {
    if (alert?.action) {
      alert.action(); 
    }
    setAlert(null); 
  };

  useEffect(() => {
    usuarioExternoService.logout();
    const getApiVersion = async () => {
      try {
        const response = await versaoSistemaService.getVersaoApi();
        setApiVersion(response.data); 
      } catch (err) {
        console.error('Erro ao buscar versão da API', err);
      }
    };
    getApiVersion();
  }, []);

  const logoPadrao = process.env.REACT_APP_LOGO || '/BPO/logomarca.png';
  let logoParaExibir = layout.logoBase64 || layout.logoUrl || logoPadrao; 

  if (pageLoading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: '#fff' }}>
        <Spinner animation="border" variant="secondary" />
      </Container>
    );
  }

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={handleCloseAlert} />)}
      <div style={{
        backgroundImage: `url('/imgBackGround.webp')`, backgroundSize: 'cover', backgroundPosition: 'center',
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1, filter: 'blur(8px)', opacity: 0.6
      }}
      />
      <Container fluid className="d-flex justify-content-center align-items-center vh-100 responsive">
        <Row className="w-100" >
          <Col md={4} className="mx-auto" style={{ display: 'flex', padding: '4rem', flexDirection: 'column', alignItems: 'center', backgroundColor: 'white', justifyContent: 'center', borderRadius: '5px', boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)', gap: '5px 0px' }}>
            <div className="text-center mb-4">
              <Image width={"280px"} src={logoParaExibir} fluid />
            </div>
            <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <Form.Group className="md-6">
                    <StyledInput
                      type="text"
                      placeholder="Digite o seu usuário"
                      name="login"
                      ref={loginRef}
                      className="mb-3"
                      autoComplete="username"
                    />
                  </Form.Group>

                  <Form.Group controlId="senha">
                    <StyledInput
                      type="password"
                      placeholder="Digite a sua senha"
                      name="senha"
                      className="mb-4"
                      ref={senhaRef}
                      autoComplete="password"
                    />
                  </Form.Group>

                  {showOriginadorInput && (
                    <Form.Group controlId="originador" className="mb-4 fade-in">
                        <StyledSelect
                          ref={originadorRef}
                          value={originadorSelecionado}
                          onChange={(e) => setOriginadorSelecionado(e.target.value)}
                        >
                          <option value="">Selecione a {Originador.DESCRITIVO_GERAL}</option>
                          {listaOriginadores.map((orig: any) => (
                             <option key={orig.id} value={orig.idOrigem}>
                               {orig.nomeOrigem} 
                             </option>
                          ))}
                        </StyledSelect>
                    </Form.Group>
                  )}
              
              <Botao
                type="submit"
                text={showOriginadorInput ? "Confirmar Login" : "Entrar"} 
                onClick={handleSubmit}
                disabled={loading}
                loading={loading}
              />

              <div className="mt-3" >
                <Button style={{ color: 'black', textDecoration: 'none' }}
                  variant="link"
                  onClick={() => setAlert({ message: 'Entre em contato com suporte!', type: 'warning' })}
                >
                  <EsqueceuSenha>
                    Esqueceu sua senha?
                  </EsqueceuSenha>
                </Button>
              </div>
            </Form>
            <Rodape />
          </Col>
        </Row>
      </Container>
    </>

  );
};

export default LoginComponent;

const StyledInput = styled(Form.Control)`
  color: black;
  font-size: 18px; 
  background-color: transparent;
  border: 2px solid #6a6a6a;
  border-radius: 8px;
  padding: 16px 20px;
  width: 100%;
  height: 50px; 
  box-shadow: none; 
  cursor: pointer;
  
  @media (max-width: 576px) {
    font-size: 12px; 
    padding: 5px 15px; 
    width: 120%; 
    height: 45px;
  }
`;

const StyledSelect = styled(Form.Select)`
  color: black;
  font-size: 18px; 
  background-color: transparent;
  border: 2px solid #6a6a6a;
  border-radius: 8px;
  padding: 0px 20px;
  width: 100%;
  height: 50px; 
  box-shadow: none;
  cursor: pointer;
  
  @media (max-width: 576px) {
    font-size: 12px; 
    padding: 0px 15px; 
    width: 120%; 
    height: 45px;
  }
`;

const EsqueceuSenha = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0px 20px;
  align-items: center';
  border-color: white;
  color: black;
  background-color: white;
  font-size: 16px; 
  text-decoration: none;
  

  @media (max-width: 576px) {
    font-size: 12px; 
    width: 120%; 
    height: 45px; 
    margin-left: 15px;
}`;